import { Button, Card, Heading, IconButton, Input, Text, toast } from "@overmap-ai/blocks"
import {
	OrganizationAccessLevel,
	type Payload,
	type ProcedureType,
	ABBREVIATION_MAX_LENGTH,
	ABBREVIATION_MIN_LENGTH,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectProcedureTypeById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { TITLE_MAX_LENGTH } from "../../constants"
import { useTextConfirmationDialog } from "../../contexts/hooks"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.settings,
		label: "General",
		description: "Description and broader settings",
		from: "/$organizationId/settings/procedure-types/$procedureTypeId/",
		to: "/$organizationId/settings/procedure-types/$procedureTypeId/general",
	},
	{
		icon: modelIconMapping.step,
		label: "Steps",
		description: "Manage procedure type steps",
		from: "/$organizationId/settings/procedure-types/$procedureTypeId/",
		to: "/$organizationId/settings/procedure-types/$procedureTypeId/steps",
	},
	{
		icon: modelIconMapping.form,
		label: "Fields",
		description: "Manage procedure type fields",
		from: "/$organizationId/settings/procedure-types/$procedureTypeId/",
		to: "/$organizationId/settings/procedure-types/$procedureTypeId/fields",
	},
	{
		icon: modelIconMapping.attachment,
		label: "Attachments",
		description: "Manage procedure type attachments",
		from: "/$organizationId/settings/procedure-types/$procedureTypeId/",
		to: "/$organizationId/settings/procedure-types/$procedureTypeId/attachments",
	},
]

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/")

export const ProcedureTypeSettingsDetailView = memo(() => {
	const { procedureTypeId } = routeApi.useParams()

	const procedureType = useAppSelector(selectProcedureTypeById(procedureTypeId))

	if (!procedureType) {
		throw new Error(`expected procedure type to be defined, got ${procedureType}`)
	}

	const navigate = useNavigate()
	const openTextConfirmationDialog = useTextConfirmationDialog()
	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchProcedureType = useCallback(
		(payload: Partial<Payload<ProcedureType>>) => {
			void sdk.procedureTypes.update({ uuid: procedureType.uuid, ...payload })
		},
		[procedureType.uuid],
	)

	const handleDelete = useCallback(() => {
		openTextConfirmationDialog((close) => ({
			title: `Delete ${procedureType.name}`,
			description: "Are you sure you want to delete this procedure type?",
			confirmString: "delete",
			confirmMessage: "Type 'delete' to confirm",
			actionLabel: "Delete",
			onClose: close,
			onConfirm: async () => {
				await navigate({
					from: "/$organizationId/settings/procedure-types/$procedureTypeId/",
					to: "/$organizationId/settings/procedure-types",
				})

				sdk.procedureTypes.delete(procedureType.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete procedure type",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		}))
	}, [navigate, openTextConfirmationDialog, procedureType.name, procedureType.uuid])

	const handleNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			handlePatchProcedureType({ name: e.target.value })
		},
		[handlePatchProcedureType],
	)

	const handleAbbrInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			handlePatchProcedureType({ abbr: e.target.value })
		},
		[handlePatchProcedureType],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(procedureType.name, handleNameInputBlur, true)

	const abbrInputProps = useBlurInput<HTMLInputElement>(procedureType.abbr, handleAbbrInputBlur, true)

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{procedureType.name}</Heading>
					<Text size="sm" accentColor="base">
						Manage procedure type settings.
					</Text>
				</div>

				<Card className="flex w-full flex-col gap-2" size="sm">
					<div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-2">
						<div className="flex w-full grow flex-col gap-1">
							<Text accentColor="base" size="sm">
								Icon & name
							</Text>
							<div className="flex w-full gap-2">
								<IconColorPickerPopover
									className="w-[340px]"
									color={procedureType.color}
									onColorChange={(color) => {
										if (!color) return
										handlePatchProcedureType({ color })
									}}
									selectedIcon={procedureType.icon}
									onIconChange={(icon) => {
										if (!icon) return
										handlePatchProcedureType({ icon })
									}}
									size="sm"
								>
									<IconButton
										variant="soft"
										size="md"
										accentColor="base"
										disabled={!isOrganizationAdmin}
									>
										<LuDynamicIcon
											icon={procedureType.icon as IconType}
											color={procedureType.color}
										/>
									</IconButton>
								</IconColorPickerPopover>
								<Input.Root className="grow" variant="outline">
									<Input.Field
										placeholder="Enter a name"
										maxLength={TITLE_MAX_LENGTH}
										disabled={!isOrganizationAdmin}
										{...nameInputProps}
									/>
								</Input.Root>
							</div>
						</div>
						<div className="flex w-full grow flex-col gap-1">
							<Text accentColor="base" size="sm">
								Abbreviation
							</Text>
							<Input.Root className="grow" variant="outline">
								<Input.Field
									minLength={ABBREVIATION_MIN_LENGTH}
									maxLength={ABBREVIATION_MAX_LENGTH}
									placeholder="Enter an abbreviation"
									disabled={!isOrganizationAdmin}
									{...abbrInputProps}
								/>
							</Input.Root>
						</div>
					</div>
				</Card>

				<RouteLinksCard routes={routes} />

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Button className="w-max" size="sm" accentColor="danger" onClick={handleDelete}>
						Delete
					</Button>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})
ProcedureTypeSettingsDetailView.displayName = "ProcedureTypeSettingsDetailView"
